import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subject, Subscription } from 'rxjs';
import Swal from 'sweetalert2';

import { AuthService } from '@app/core/auth';
import { ClientService } from '@app/core/client/services/client.service';
import { CategoryService } from '@app/core/products/services/category.service';
import { RFQ, RfqService } from '@app/core/quotes';
import { SettingsService } from '@app/core/settings/services/settings.service';
import { StaffService } from '@app/core/staff';
import { ExportRfqService } from '@app/shared/services/export-rfq.service';
import { trimWala } from '@app/shared/utils/string.utils';

@Component({
  selector: 'cnc-archived-rfqs',
  templateUrl: './archived-rfqs.component.html',
  styleUrls: ['./archived-rfqs.component.scss'],
})
export class ArchivedRfqsComponent implements OnInit, OnDestroy {
  initialize :boolean = false;
  length = 0;
  pageSize = 100;
  pageSizeOptions :number[] = [5, 10, 25, 100];
  pageTitle :string = this.translate.instant('TITLES.REQUEST_FOR_QUOTES');
  searchKey :string = '';

  filter :any = {};
  checked :any = [];
  showFilter :boolean = false;
  checkedAll :boolean = false;
  checkedCount :number = 0;

  client$ :Observable<any[]>;
  clientLoading = false;
  clientInput$ = new Subject<string>();

  goTeam :any[] = [];
  goStaff :any[] = [];
  warehouses :any[] = [];
  locations :any[] = [];
  locationsRaw :any[] = [];

  settings :any = {};
  rfqRecord :any = { data: [] };
  gettingRecords :boolean;
  subscription :Subscription;

  constructor(
    public authService :AuthService,
    public rfqService :RfqService,
    private translate :TranslateService,
    public pcatService :CategoryService,
    private router :Router,
    public exportRfq :ExportRfqService,
    public staffService :StaffService,
    public clientSvc :ClientService,
    public settingsSvc :SettingsService,
    private toastr :ToastrService,
    private cd :ChangeDetectorRef,
  ) {}

  params :any = {
    per_page: 100,
    page: 1,
  };

  setOrder(order :string) {
    if (this.filter.order && this.filter.order == order) {
      this.filter.order = '-' + order;
      this.filter.sort = 'desc';
    } else {
      this.filter.order = order;
      this.filter.sort = 'asc';
    }

    this.getRecords(false, 1);
  }

  ngOnInit() {
    this.settingsSvc.masterfile$.subscribe((set :any) => {
      this.settings = set;
    });

    this.initialize = false;
    this.getRecords();
  }

  updateLocationsList() {
    if (!this.filter.warehouses) {
      this.locations = [];
    } else {
      this.locations = this.locationsRaw.filter(
        (row) => this.filter.warehouses.indexOf(row.parent_id) >= 0,
      );
    }
  }

  showDetails(rfq :RFQ, v ?:string) {
    this.rfqService.setActive(rfq, true);
    if (v) {
      this.router.navigate(['inventory', rfq.id, 'view'], {
        queryParams: { v: v },
      });
    } else {
      this.router.navigate(['inventory', rfq.id, 'view']);
    }
  }

  deleteSelected() {
    const ids :any[] = [];
    this.checked.forEach((value :any, id :any) => {
      if (value) {
        ids.push(id);
      }
    });
    Swal.fire({
      title: 'Confirm Delete?',
      text: 'Delete selected SKUs?',
      type: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: this.translate.instant('BUTTONS.CONFIRM_DELETE_TEXT'),
    }).then((result) => {
      if (result.value) {
        this.rfqService.deleteArchivedRfqs({ ids: ids }).subscribe((resp :any) => {
          this.toastr.success('Successfully deleted SKUs.', 'Success');
          this.getRecords();
        });
      }
    });
  }

  gotoPurchaseOrders(rfq) {
    this.router.navigateByUrl('/orders?sku=' + rfq.internal_sku);
  }

  countChecked() {
    this.checkedCount = this.checked.filter((x) => {
      return x;
    }).length;
  }

  checkAll() {
    this.rfqRecord.data.forEach((r) => {
      this.checked[r.id] = this.checkedAll;
    });
    this.countChecked();
  }

  getRecordTimeout() {
    if (this.initialize) {
      return;
    }
    setTimeout(() => {
      this.getRecords(false, 1);
    }, 10);
  }

  getRecords(event ?:any, page ?:number, paramsOnly ?:boolean) {
    // let params: any = { page: 1, per_page: this.pageSize }
    if (this.initialize) {
      return;
    }
    if (page) {
      this.params.page = page;
    }

    if (this.searchKey && this.searchKey.trim().length > 0) {
      this.params.keyword = this.searchKey;
    } else {
      delete this.params.keyword;
    }

    this.params = Object.assign(this.params, this.filter);
    delete this.params.client;
    if (!this.filter.product || this.filter.product.trim().length == 0) {
      delete this.params.product;
    }
    if (!this.filter.sku || this.filter.sku.trim().length == 0) {
      delete this.params.sku;
    }
    if (this.filter.rfq_dept == 0) {
      delete this.params.rfq_dept;
    }
    if (this.params.order) {
      this.params.order = trimWala(this.params.order, '-');
    }
    if (this.params.starred < 0) {
      delete this.params.starred;
    }
    if (event) {
      this.params.page = event.pageIndex + 1;
      this.params.per_page = event.pageSize;
    }

    this.params.product_type_id = 2;
    // set state
    this.params.status = -1;

    this.gettingRecords = true;
    this.subscription = this.rfqService
      .getArchivedRfqs(this.params)
      .subscribe((resp :any) => {
        resp.data.forEach((row) => {
          // row.unit_costs = this.rfqService.getUnitCosts(row.qty_available, row.latest_purchase_orders, (row.base_price ? row.base_price.price : 0));
          row.unit_costs = this.rfqService.getSkuUnitCosts(row);
          try {
            row.product_variants = JSON.parse(row.product_variants);
          } catch (e) {}
          try {
            row.shipping_weight = JSON.parse(row.shipping_weight);
            row.dimensional_weight = JSON.parse(row.dimensional_weight);
          } catch (e) {}

          try {
            row.product_link = JSON.parse(row.product_link);
          } catch (e) {
            if (row.product_link.trim().length > 0) {
              row.product_link = [{ lv: row.product_link }];
            }
          }
          try {
            row.custom_fields = JSON.parse(row.custom_fields);
          } catch (e) {}
          try {
            row.materials = JSON.parse(row.materials);
          } catch (e) {}
        });

        this.gettingRecords = false;
        this.rfqRecord = resp;
        this.cd.detectChanges();
      });
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  getQuoteType(id :number) {
    const qt :any = this.rfqService.quoteTypes.find((row) => {
      return row.id == id;
    });

    return qt.text;
  }

  resetFields() {
    delete this.params.sku;
    delete this.params.date;
    delete this.params.client;
    delete this.params.product;
    delete this.params.starred;
    delete this.params.category;
    delete this.params.goteam;
    delete this.params.sourcing;
    delete this.params.rfq_quote_type;
    delete this.params.rfq_state;
    delete this.params.rfq_dept;
    delete this.params.discontinued;
    delete this.params.qty_lvl;
    delete this.params.warehouses;
    delete this.params.locations;
    delete this.params.internal_use_only;
    this.filter = {};
    this.setOrder('created_at');
    // this.getRecords();
  }

  delete(rfq :RFQ, idx :any) {
    Swal.fire({
      title: 'Confirm Delete?',
      text: 'Delete SKU for product ' + rfq.product_name + '?',
      type: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: this.translate.instant('BUTTONS.CONFIRM_DELETE_TEXT'),
    }).then((result) => {
      if (result.value) {
        this.rfqService
          .deleteArchivedRfqs({ ids: [rfq.id] })
          .subscribe((resp :any) => {
            this.toastr.success('Successfully deleted SKU.', 'Success');
            this.getRecords();
          });
      }
    });
  }

  trackClientByFn(item :any) {
    return item.id;
  }
}
